<template>
	<Teleport to="#popups">
		<div v-if="shouldMountDrawer" class="assistant-drawer-wrapper" :class="{hidden: !isDrawerVisible}">
			<div class="assistant-drawer">
				<div class="drawer-header">
					<div class="header-title">
						<div class="icon-assistant" v-html="svgStore.get('iconChatBot')"></div>
						<span>{{ getString('chat_bot') }}</span>
					</div>
					<div class="button-close" @click="closeDrawer">
						<div class="icon-close" v-html="svgStore.get('iconX')"></div>
					</div>
				</div>
				<div class="drawer-content">
					<ChatSidebar :force-compressed="true" />
					<ChatContent />
				</div>
			</div>
		</div>
		<div v-if="showFloatingButton" class="floating-resume-button" :class="{ dragging: isDragging }" :style="floatingButtonStyle" @mousedown="onDragStart" @touchstart="onDragStart" @click="openFromFloatingButton">
			<div v-if="openAIChatsStore.hasUnreadMessage" class="indicator-unread"></div>
			<div v-else class="button-dismiss" @click.stop="dismissFloatingButton">
				<div class="icon-dismiss" v-html="svgStore.get('iconX')"></div>
			</div>
			<div class="icon-chat" v-html="svgStore.get('iconChatBot')"></div>
		</div>
		<AppBottomSheet
			:force-width="bottomSheetWidth"
			:active="openAIChatsStore.showImagePicker"
			@toggling-popup="toggleImagePicker"
		>
			<ModalHeader :title="getString('add_images')" />
			<div class="image-picker-content">
				<AppDropzone
					:label="getString('upload_images')"
					:max-bytes="600 * 1024"
					:max-width="1024"
					:max-height="1024"
					:max-files="3"
					:files="openAIChatsStore.attachedImages"
					@upload-complete="handleImagesChanged"
					@removed-file="(url, urls) => handleImagesChanged(urls)"
				/>
			</div>
		</AppBottomSheet>
	</Teleport>
</template>

<script>
import ChatSidebar from "@/console-new/components/help/chat/ChatSidebar.vue";
import ChatContent from "@/console-new/components/help/chat/ChatContent.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppDropzone from "@/shared/components/AppDropzone.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useOpenAIChatsStore, useUserStore} from "@/console-new/stores";
import {useLanguageStore, useSvgStore} from "@/shared/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		let openAIChatsStore = useOpenAIChatsStore();
		let userStore = useUserStore();
		let languageStore = useLanguageStore();
		let svgStore = useSvgStore();
		return { openAIChatsStore, userStore, svgStore, getString: languageStore.getString };
	},
	name: "AssistantDrawer",
	mixins: [WindowWidth],
	components: { ChatSidebar, ChatContent, AppBottomSheet, AppDropzone, ModalHeader },
	data() {
		return {
			escListener: null,
			floatingButtonDismissed: false,
			isDragging: false,
			didDrag: false,
			dragStartX: 0,
			dragStartY: 0,
			buttonX: null,
			buttonY: null,
			initialButtonX: 0,
			initialButtonY: 0,
			dragThreshold: 5,
			isDrawerMounted: false,
		}
	},
	computed: {
		shouldMountDrawer() {
			return this.openAIChatsStore.drawerOpen || this.isDrawerMounted;
		},
		isDrawerVisible() {
			return this.openAIChatsStore.drawerOpen && this.isDrawerMounted;
		},
		showFloatingButton() {
			return !this.openAIChatsStore.drawerOpen
				&& this.windowWidth < 920
				&& this.openAIChatsStore.activeChatId
				&& this.openAIChatsStore.activeChatId !== 'new'
				&& !this.floatingButtonDismissed;
		},
		floatingButtonStyle() {
			if (this.buttonX === null) return {};
			return {
				right: 'auto',
				bottom: 'auto',
				left: `${this.buttonX}px`,
				top: `${this.buttonY}px`,
			};
		},
		bottomSheetWidth() {
			if(this.windowWidth < 450) {
				return "100%";
			} else if(this.windowWidth < 550) {
				return "450px";
			} else {
				return "550px";
			}
		},
	},
	methods: {
		closeDrawer() {
			this.openAIChatsStore.closeDrawer();
		},
		dismissFloatingButton() {
			this.floatingButtonDismissed = true;
		},
		openFromFloatingButton() {
			if (this.didDrag) {
				this.didDrag = false;
				return;
			}
			this.openAIChatsStore.openDrawer();
		},
		onDragStart(e) {
			const touch = e.touches ? e.touches[0] : e;
			this.dragStartX = touch.clientX;
			this.dragStartY = touch.clientY;

			if (this.buttonX === null) {
				const rect = e.currentTarget.getBoundingClientRect();
				this.buttonX = rect.left;
				this.buttonY = rect.top;
			}

			this.initialButtonX = this.buttonX;
			this.initialButtonY = this.buttonY;

			document.addEventListener('mousemove', this.onDragMove);
			document.addEventListener('mouseup', this.onDragEnd);
			document.addEventListener('touchmove', this.onDragMove, { passive: false });
			document.addEventListener('touchend', this.onDragEnd);
		},
		onDragMove(e) {
			e.preventDefault();
			const touch = e.touches ? e.touches[0] : e;
			const deltaX = touch.clientX - this.dragStartX;
			const deltaY = touch.clientY - this.dragStartY;

			if (!this.isDragging && (Math.abs(deltaX) > this.dragThreshold || Math.abs(deltaY) > this.dragThreshold)) {
				this.isDragging = true;
				this.didDrag = true;
			}

			if (this.isDragging) {
				this.buttonX = this.initialButtonX + deltaX;
				this.buttonY = this.initialButtonY + deltaY;
				this.clampPosition();
			}
		},
		onDragEnd() {
			document.removeEventListener('mousemove', this.onDragMove);
			document.removeEventListener('mouseup', this.onDragEnd);
			document.removeEventListener('touchmove', this.onDragMove);
			document.removeEventListener('touchend', this.onDragEnd);

			this.isDragging = false;

			if (this.didDrag) {
				// Use requestAnimationFrame to ensure the transition is applied before snapping
				requestAnimationFrame(() => {
					this.snapToEdge();
					this.savePosition();
				});
			}
		},
		clampPosition() {
			const buttonSize = 56;
			const margin = 14;
			this.buttonX = Math.max(margin, Math.min(window.innerWidth - buttonSize - margin, this.buttonX));
			this.buttonY = Math.max(margin, Math.min(window.innerHeight - buttonSize - margin, this.buttonY));
		},
		snapToEdge() {
			const buttonSize = 56;
			const margin = 18;

			const distLeft = this.buttonX;
			const distRight = window.innerWidth - (this.buttonX + buttonSize);
			const distTop = this.buttonY;
			const distBottom = window.innerHeight - (this.buttonY + buttonSize);

			const minDist = Math.min(distLeft, distRight, distTop, distBottom);

			if (minDist === distLeft) {
				this.buttonX = margin;
			} else if (minDist === distRight) {
				this.buttonX = window.innerWidth - buttonSize - margin;
			} else if (minDist === distTop) {
				this.buttonY = margin;
			} else {
				this.buttonY = window.innerHeight - buttonSize - margin;
			}

			this.clampPosition();
		},
		savePosition() {
			sessionStorage.setItem('floatingButtonPos', JSON.stringify({
				x: this.buttonX,
				y: this.buttonY
			}));
		},
		loadPosition() {
			const saved = sessionStorage.getItem('floatingButtonPos');
			if (saved) {
				const pos = JSON.parse(saved);
				this.buttonX = pos.x;
				this.buttonY = pos.y;
				this.clampPosition();
				this.snapToEdge();
			}
		},
		onWindowResize() {
			if (this.buttonX !== null) {
				this.clampPosition();
				this.snapToEdge();
			}
		},
		fetchChats() {
			if(this.userStore.currBusinessId) {
				this.openAIChatsStore.getChats();
			}
		},
		toggleImagePicker(isActive) {
			if(!isActive) {
				this.openAIChatsStore.closeImagePicker();
			}
		},
		handleImagesChanged(files) {
			this.openAIChatsStore.attachedImages = files || [];
		},
	},
	mounted() {
		this.escListener = (e) => {
			if (e.keyCode === 27 && this.openAIChatsStore.drawerOpen) {
				this.closeDrawer();
			}
		}
		document.addEventListener('keyup', this.escListener);
		this.loadPosition();
		window.addEventListener('resize', this.onWindowResize);
	},
	beforeUnmount() {
		document.removeEventListener('keyup', this.escListener);
		window.removeEventListener('resize', this.onWindowResize);
		if(this.openAIChatsStore.activeChatId) {
			this.openAIChatsStore.unsubscribeFromChat(this.openAIChatsStore.activeChatId);
			this.openAIChatsStore.activeChatId = null;
		}
	},
	watch: {
		'openAIChatsStore.drawerOpen': function (newVal, oldVal) {
			if(newVal && !oldVal) {
				// Mount hidden first, then show after paint to trigger animation
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						this.isDrawerMounted = true;
					});
				});
				let activeChatId = this.openAIChatsStore.activeChatId;
				let activeChat = this.openAIChatsStore.activeChat;
				if(activeChatId && activeChatId !== 'new') {
					// If we already have the chat with messages, just resubscribe and refresh messages
					// without clearing the store (avoids UI flicker on mobile reopen)
					if(activeChat?.messages?.length) {
						this.openAIChatsStore.getChatMessages(activeChatId);
					} else {
						this.openAIChatsStore.getChats(() => {
							this.openAIChatsStore.getChatMessages(activeChatId);
						});
					}
					this.openAIChatsStore.subscribeToChat(activeChatId);
				} else {
					this.fetchChats();
				}
			}
			if(!newVal && oldVal) {
				this.floatingButtonDismissed = false;
				// Wait for the close animation (300ms) before unmounting
				setTimeout(() => {
					if (!this.openAIChatsStore.drawerOpen) {
						this.isDrawerMounted = false;
					}
				}, 350);
			}
		},
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== oldVal && this.openAIChatsStore.drawerOpen) {
				this.fetchChats();
			}
		},
		'openAIChatsStore.activeChatId': function (newVal, oldVal) {
			if(oldVal && oldVal !== 'new') {
				this.openAIChatsStore.unsubscribeFromChat(oldVal);
			}
			if(newVal && newVal !== 'new') {
				this.openAIChatsStore.subscribeToChat(newVal);
			}
			if(newVal !== oldVal) {
				this.floatingButtonDismissed = false;
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.assistant-drawer-wrapper {
	--floating-margin: 16px;
	position: fixed;
	top: var(--floating-margin);
	right: var(--floating-margin);
	height: calc(100% - calc(2 * var(--floating-margin)));
	z-index: 1000;
	pointer-events: none;

	.assistant-drawer {
		position: absolute;
		top: 0;
		right: 0;
		height: 100%;
		width: 500px;
		max-width: 100vw;
		background-color: var(--color-bg);
		box-shadow: -5px 0 35px var(--color-box-shadow-3);
		display: flex;
		flex-direction: column;
		pointer-events: auto;
		transform: translateX(0);
		transition: transform 0.3s $transition;
		border-radius: 13px;
		overflow: hidden;

		.drawer-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 15px 20px;
			border-bottom: 1px solid var(--color-alpha-4);
			background-color: var(--color-bg-primary-1);

			.header-title {
				display: flex;
				align-items: center;
				gap: 10px;

				.icon-assistant {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 24px;
					height: 24px;

					:deep(svg) {
						width: 24px;
						height: 24px;

						.fill {
							fill: var(--color-primary);
						}
						.stroke {
							stroke: var(--color-primary);
						}
					}
				}

				span {
					font-size: 18px;
					font-weight: 600;
					color: var(--color-text-1);
				}
			}

			.button-close {
				display: flex;
				justify-content: center;
				align-items: center;
				width: 36px;
				height: 36px;
				background-color: var(--color-alpha-1);
				border-radius: 10px;
				cursor: pointer;
				transition: background-color 0.3s $transition;

				.icon-close {
					display: flex;
					align-items: center;
					justify-content: center;

					:deep(svg) {
						width: 12px;
						height: 12px;

						.stroke {
							stroke: var(--color-txt-1);
						}
					}
				}

				&:hover {
					background-color: var(--color-alpha-2);
				}
			}
		}

		.drawer-content {
			display: flex;
			flex: 1;
			overflow: hidden;
			position: relative;
		}
	}


	@media screen and (max-width: 560px) {
		--floating-margin: 0px;

		.assistant-drawer {
			width: 100vw;
			border-radius: 0;
		}
	}
	&.hidden {
		.assistant-drawer {
			transform: translateX(calc(100% + var(--floating-margin) * 2));
			pointer-events: none;
		}
	}
}

.floating-resume-button {
	position: fixed;
	bottom: 14px;
	right: 18px;
	z-index: 999;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 56px;
	height: 56px;
	background-color: var(--color-primary);
	border-radius: 18px;
	box-shadow: 0 4px 12px var(--color-box-shadow-3);
	cursor: grab;
	transition: left 0.3s ease-out, top 0.3s ease-out, transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
	touch-action: none;
	user-select: none;

	&:active:not(.dragging) {
		transform: scale(0.95);
	}

	&.dragging {
		cursor: grabbing;
		transition: none;
	}

	.icon-chat {
		display: flex;
		align-items: center;
		justify-content: center;

		:deep(svg) {
			width: 24px;
			height: 24px;

			.fill {
				fill: var(--color-txt-white);
			}
			.stroke {
				stroke: var(--color-txt-white);
			}
		}
	}

	.indicator-unread {
		position: absolute;
		top: -2px;
		right: -2px;
		width: 14px;
		height: 14px;
		background-color: var(--color-danger);
		border-radius: 50%;
		box-shadow: 0 2px 6px var(--color-box-shadow-1);
		animation: pulse-unread 2s ease-in-out infinite;
	}

	@keyframes pulse-unread {
		0%, 100% {
			transform: scale(1);
			opacity: 1;
		}
		50% {
			transform: scale(1.1);
			opacity: 0.8;
		}
	}

	.button-dismiss {
		position: absolute;
		top: -4px;
		right: -4px;
		display: flex;
		justify-content: center;
		align-items: center;
		width: 20px;
		height: 20px;
		background-color: var(--color-bg);
		border-radius: 50%;
		box-shadow: 0 2px 6px var(--color-box-shadow-1);
		cursor: pointer;
		transition: background-color 0.2s ease-in-out;

		&:hover {
			background-color: var(--color-alpha-2);
		}

		.icon-dismiss {
			display: flex;
			align-items: center;
			justify-content: center;

			:deep(svg) {
				width: 8px;
				height: 8px;

				.stroke {
					stroke: var(--color-txt-1);
				}
			}
		}
	}
}

.image-picker-content {
	width: 100%;

	:deep(.dropzone-container) {
		width: 100%;
	}
}
</style>
